import React, {Fragment, useContext, useEffect} from 'react';
import {ApiUtility} from "../common/ApiUtility";
import {OverlayLoadingIndicator} from "../common/OverlayLoadingIndicator";
import {ListPaginationOptions} from "../common/ListPaginationOptions";
import {CreateSubscription} from "./CreateSubscription";
import {UpdateSubscription} from "./UpdateSubscription";
import {GlobalContext} from "../admin-frame/AdminFrame";
import {CommonListPageHeader} from "../common/CommonListPageHeader";
import {ConditionalRenderer} from "../common/ConditionalRenderer";
import {useRenderableData} from "../common/hooks/useRenderableData";
import {UrlUtility} from "../common/UrlUtility";
import {MessageDisplayerUtility} from "../common/MessageDisplayerUtility";
import {DateUtility} from "../common/DateUtility";
import {PillButton} from "../common/PillButton";
import {SubscriptionDataUtility} from "../common/SubscriptionDataUtility";
import {InvoiceDataUtility} from "../common/InvoiceDataUtility";
import {SingleInvoice} from "../invoices/SingleInvoice";
import {ConfirmCancelModal} from "../common/ConfirmCancelModal";
import _ from "lodash";

export const SubscriptionsListPage = function (props) {
    const context = useContext(GlobalContext);
    const urlBase = '/subscriptions';
    const renderableData = useRenderableData({
        selectedPage: 1,
        searchText: '',
        showCreateSubscriptionModal: false,
        showUpdateSubscriptionModal: false,
        updateSubscriptionId: null,
        showUpdateInvoiceModal: false,
        showConfirmSyncModal: false,
        updateInvoiceId: null,
        subscriptionList: [],
        pageCount: 0,
        loading: false,

        prevUrlQueryString: null,
        prevParams: null,
        prevShowCreateSubscriptionModal: null
    })

    // Update state if URL changes.
    if (
        (props.location?.search) !== renderableData.prevUrlQueryString ||
        (props.match?.params) !== renderableData.prevParams ||
        props.showCreateSubscriptionModal !== renderableData.prevShowCreateSubscriptionModal
    ) {
        let selectedPage = 1;
        let searchText = '';
        let updateSubscriptionId = null;
        let showUpdateSubscriptionModal = false;
        let updateInvoiceId = null;
        let showUpdateInvoiceModal = false;

        let routeParams = props.match && props.match.params;
        if (
            routeParams?.subscriptionId &&
            routeParams?.subscriptionId !== 'add' &&
            props.match.path.indexOf('/add/') <= -1
        ) {
            showUpdateSubscriptionModal = true;
            updateSubscriptionId = routeParams['subscriptionId'];
        }

        if (routeParams?.invoiceId) {
            showUpdateInvoiceModal = true;
            updateInvoiceId = routeParams.invoiceId;
        }

        let queryParams = UrlUtility.getQueryParamsFromProps(props);
        if (queryParams.page) {
            selectedPage = queryParams.page;
        }

        if (queryParams.searchText) {
            searchText = queryParams.searchText;
        }

        renderableData.updateAndRender({
            selectedPage: selectedPage,
            searchText: searchText,
            showCreateSubscriptionModal: props.showCreateSubscriptionModal || false,
            showUpdateSubscriptionModal: showUpdateSubscriptionModal || false,
            updateSubscriptionId: updateSubscriptionId,

            showUpdateInvoiceModal: showUpdateInvoiceModal || false,
            updateInvoiceId: updateInvoiceId,

            prevUrlQueryString: props.location?.search,
            prevParams: props.match && props.match.params,
            prevShowCreateSubscriptionModal: props.showCreateSubscriptionModal
        })
    }

    // Trigger reload of list data on mount and if certain values change in state.
    useEffect(
        function () {
            reloadList();
        },
        [
            '' + renderableData.selectedPage,
            '' + renderableData.searchText,
            JSON.stringify(renderableData.filters)
        ]
    );

    function reloadList(callback) {
        renderableData.updateAndRender({loading: true})
        ApiUtility.getSubscriptionsList(
            renderableData.selectedPage,
            renderableData.searchText,
            renderableData.filters
        )
            .then(function (listData) {
                renderableData.updateAndRender({
                    ...listData,
                    loading: false
                });
                if (callback) {
                    callback();
                }
            })
            .catch(function (error) {
                renderableData.updateAndRender({loading: false});
                console && console.error(error);
                MessageDisplayerUtility.error(`An error occurred while loading the subscription list.`);
            });
    }

    function goToAddSubscriptionModal() {
        context.navigator.navigateTo(`${urlBase}/add${props.location.search}`);
    }

    function goToUpdateSubscriptionModal(subscriptionId) {
        context.navigator.navigateTo(`${urlBase}/${subscriptionId}${props.location.search}`);
    }

    function goToUpdateInvoiceModal(invoiceId) {
        context.navigator.navigateTo(`${urlBase}/invoices/${invoiceId}${props.location.search}`);
    }

    function goToSubscriptionInvoicesListPage() {
        context.navigator.navigateTo("/subscription-invoices");
    }

    function closeModals() {
        context.navigator.navigateTo(`${urlBase}${props.location.search}`);
    }

    function afterSuccessfulSubscriptionSave(subscriptionId) {
        closeModals()
        reloadList(
            () => goToUpdateSubscriptionModal(subscriptionId)
        );
    }

    function afterSuccessfulInvoiceSave(invoiceId) {
        closeModals()
        reloadList(
            () => goToUpdateInvoiceModal(invoiceId)
        );
    }

    function onClickSync() {
        renderableData.updateAndRender({showConfirmSyncModal: true})
    }

    function closeConfirmSyncModal() {
        renderableData.updateAndRender({showConfirmSyncModal: false})
    }

    function performSync() {
        renderableData.updateAndRender({loading: true})
        ApiUtility.syncAllSubscriptionInvoices()
            .then((response) => {
                closeConfirmSyncModal();
                MessageDisplayerUtility.success(`Successfully synced subscription invoices.`);
                reloadList();
            })
            .catch((err) => {
                renderableData.updateAndRender({loading: false});
                console && console.error(err);
                MessageDisplayerUtility.error(`An error occurred while syncing the subscription invoices.`);
            });
    }

    function renderSubscriptionsListDisplay(subscriptionsList) {
        let subscriptionsListDisplay = [];

        for (let i = 0; i < subscriptionsList.length; i++) {
            const subscription = subscriptionsList[i];
            let durationDisplay =
                (DateUtility.dbDateToLocalDisplayDate(subscription.start_date) || 'Unknown') +
                ' - ' +
                (DateUtility.dbDateToLocalDisplayDate(subscription.end_date) || 'Forever')

            let description = subscription.description || '';
            if (description.length > 200) {
                description = description.substring(0, 200) + '...';
            }

            let totalDisplay = SubscriptionDataUtility.getSubscriptionTotalDisplay(subscription)
            let linesDisplay = SubscriptionDataUtility.getSubscriptionLinesDisplay(subscription)

            let invoicesDisplay = (
                <div className="no-invoices-for-subscription-message">
                    No invoices found for subscription.
                </div>
            );
            if (subscription.invoices?.length > 0) {
                invoicesDisplay = [];
                let invoicesLatestFirst = _.sortBy(subscription.invoices, function (invoice) {
                    return -1 * (DateUtility.dbDateToTimestamp(invoice.created_at) || 0)
                })
                for (let x = 0; x < invoicesLatestFirst.length; x++) {
                    let invoice = invoicesLatestFirst[x];
                    let invoiceStatusClass = InvoiceDataUtility.invoiceStatusToClass(invoice.invoice_status)
                    invoicesDisplay.push(
                        <div
                            key={`${i + 1}-${x}`}
                            id={`subscriptionListInvoiceLine-${invoice.id}`}
                            className="common-list-sub-row subscription-invoice-list-row"
                            onClick={() => goToUpdateInvoiceModal(invoice.id)}
                        >
                            <div className="common-list-sub-row-content">
                                <div className="subscription-invoice-row-cell label">
                                    Invoice #{invoice.sequence_number_in_subscription}
                                </div>
                                <div className="subscription-invoice-row-cell billing-status">
                                    <div className="invoice-status-content">
                                        <div className={`invoice-status-badge ${invoiceStatusClass}`}>
                                            {invoice.invoice_status.name}
                                        </div>
                                        <div className="invoice-status-additional-info">
                                            {invoice.invoice_status.additional_info}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )
                }
            }

            subscriptionsListDisplay.push(
                <div
                    key={i + 1}
                    id={`subscriptionId-${subscription.id}`}
                    className="common-list-row common-list-values-row subscriptions-list-row"
                >
                    <div
                        className="common-list-row-main-content"
                        onClick={() => goToUpdateSubscriptionModal(subscription.id)}
                    >
                        <div className="common-list-row-content">
                            <div
                                className="common-list-row-cell common-list-row-value-cell subscriptions-list-row-cell basics"
                            >
                                <div className="subscription-basics-content">
                                    <div className="subscription-basics-top">
                                        {`Subscription for ${subscription.ext_stripe_customer_name}`}
                                    </div>
                                    <div className="subscription-basics-bottom">
                                        <span className="subscription-invoice-total-display">{totalDisplay}</span>
                                        <span className="subscription-invoice-lines-summary">{linesDisplay}</span>
                                    </div>
                                </div>
                            </div>
                            <div
                                className="common-list-row-cell common-list-row-value-cell subscriptions-list-row-cell description"
                            >{description}</div>
                            <div
                                className="common-list-row-cell common-list-row-value-cell subscriptions-list-row-cell duration"
                            >{durationDisplay}</div>
                            <div
                                className="common-list-row-cell common-list-row-value-cell subscriptions-list-row-cell status"
                            >{subscription.active ? 'Active' : 'Inactive'}</div>
                        </div>
                    </div>
                    <div className="common-list-row-sub-content">
                        {invoicesDisplay}
                    </div>
                </div>
            );
        }

        return subscriptionsListDisplay;
    }

    if (renderableData.subscriptionList) {
        return (
            <div className="common-list-page subscriptions-list-page">
                <CommonListPageHeader
                    tabs={[
                        {
                            title: 'Subscriptions',
                            onClick: () => null,
                            selected: true
                        },
                        {
                            title: 'Subscription Invoices',
                            onClick: goToSubscriptionInvoicesListPage,
                            selected: false
                        },
                    ]}
                    rightHeaderRenderer={() => (
                        <Fragment>
                            <PillButton
                                onClick={goToAddSubscriptionModal}
                                containerClasses="add-button-container"
                                buttonClasses="add-button"
                                leftIconSrc={"/static/images/plus-textcolor.svg"}
                                buttonText="ADD"
                            />
                            <PillButton
                                onClick={onClickSync}
                                containerClasses="sync-button-container subscription-list-sync-button-container"
                                buttonClasses="sync-button subscription-list-sync-button"
                                leftIconSrc={"/static/images/sync-white.svg"}
                                buttonText="SYNC ALL"
                            />
                        </Fragment>
                    )}
                />

                <div className="common-list-page-content">
                    <ConditionalRenderer
                        if={renderableData.loading}
                        renderer={() => <OverlayLoadingIndicator/>}
                    />
                    {(() => {
                        if (renderableData.subscriptionList.length > 0) {
                            return (
                                <Fragment>
                                    <div className="common-list-header">
                                        <div className="common-list-row common-list-labels-row">
                                            <div className="common-list-row-content">
                                                <div
                                                    className="common-list-row-cell common-list-row-label-cell subscriptions-list-row-cell basics"
                                                >Basics
                                                </div>
                                                <div
                                                    className="common-list-row-cell common-list-row-label-cell subscriptions-list-row-cell description"
                                                >Description
                                                </div>
                                                <div
                                                    className="common-list-row-cell common-list-row-label-cell subscriptions-list-row-cell duration"
                                                >Duration
                                                </div>
                                                <div
                                                    className="common-list-row-cell common-list-row-label-cell subscriptions-list-row-cell status"
                                                >Status
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="common-list-content">
                                        {renderSubscriptionsListDisplay(renderableData.subscriptionList)}
                                    </div>
                                    <ListPaginationOptions
                                        pageCount={renderableData.pageCount}
                                        selectedPage={renderableData.selectedPage}
                                        urlBase="/subscriptions"
                                    />
                                </Fragment>
                            );
                        } else {
                            return (
                                <div className="common-empty-list-message-container">
                                    <div className="common-empty-list-message">No subscriptions found.</div>
                                </div>
                            );
                        }
                    })()}
                </div>
                {(() => {
                    if (renderableData.showCreateSubscriptionModal) {
                        return (
                            <CreateSubscription
                                cancel={closeModals}
                                afterSuccessfulSave={afterSuccessfulSubscriptionSave}
                            />
                        );
                    } else if (renderableData.showUpdateSubscriptionModal) {
                        return (
                            <UpdateSubscription
                                cancel={closeModals}
                                afterSuccessfulSave={afterSuccessfulSubscriptionSave}
                                subscriptionId={renderableData.updateSubscriptionId}
                                onClickInvoice={goToUpdateInvoiceModal}
                            />
                        );
                    } else if (renderableData.showUpdateInvoiceModal) {
                        return (
                            <SingleInvoice
                                cancel={closeModals}
                                invoiceId={renderableData.updateInvoiceId}
                                afterSuccessfulSave={afterSuccessfulInvoiceSave}
                                goToSubscription={goToUpdateSubscriptionModal}
                            />
                        );
                    } else if (renderableData.showConfirmSyncModal) {
                        return (
                            <ConfirmCancelModal
                                cancel={closeConfirmSyncModal}
                                confirm={performSync}
                                headerText="Confirm Sync"
                                confirmButtonText="SYNC"
                                warningMessage={[
                                    "Are you sure you want to sync all subscription invoices?",
                                    "This will sync any new Stripe subscription invoices from the last 30 days to our system and QBO."
                                ]}
                            />
                        )
                    }
                })()}
            </div>
        );
    } else {
        return (
            <OverlayLoadingIndicator/>
        );
    }
};
